import { useCallback, useEffect, useState } from 'react';
import { formsApi } from '../api/formsApi';
import type { WebhookDelivery } from '../api/types';

/** Configure a form's webhook and view its delivery log (post-submit pipeline). */
export function WebhookPanel({ formId }: { formId: string }) {
  const [url, setUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [hasSecret, setHasSecret] = useState(false);
  const [headers, setHeaders] = useState('');
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const loadDeliveries = useCallback(() => {
    formsApi
      .listWebhookDeliveries(formId)
      .then(setDeliveries)
      .catch(() => {
        /* deliveries are best-effort in the UI */
      });
  }, [formId]);

  useEffect(() => {
    formsApi
      .getWebhook(formId)
      .then((cfg) => {
        setUrl(cfg.url ?? '');
        setHeaders(cfg.headers ?? '');
        setHasSecret(cfg.hasSecret);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load webhook'));
    loadDeliveries();
  }, [formId, loadDeliveries]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const cfg = await formsApi.updateWebhook(formId, {
        url: url.trim() || null,
        headers: headers.trim() || null,
        // Only send the secret when the user typed one (blank = keep current).
        secret: secret.length > 0 ? secret : null,
      });
      setHasSecret(cfg.hasSecret);
      setSecret('');
      setStatus('Webhook saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save webhook');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="secret-links">
      <h3>Webhook</h3>
      <p className="muted">
        On each submission we POST a signed JSON payload to this URL. The signature is in the{' '}
        <code>X-Forms-Signature</code> header (HMAC-SHA256 of the body using the secret). Failures
        retry with backoff (5 attempts).
      </p>

      <form className="webhook-form" onSubmit={save}>
        <label className="field">
          Endpoint URL
          <input
            type="url"
            placeholder="https://example.com/hooks/forms"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
        </label>
        <label className="field">
          Signing secret {hasSecret && <span className="muted">(set — leave blank to keep)</span>}
          <input
            type="password"
            placeholder={hasSecret ? '••••••••' : 'optional'}
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
          />
        </label>
        <label className="field">
          Extra headers (JSON object, optional)
          <textarea
            rows={2}
            placeholder='{"X-Api-Key": "…"}'
            value={headers}
            onChange={(e) => setHeaders(e.target.value)}
          />
        </label>
        <div className="actions">
          <button type="submit" disabled={busy}>Save webhook</button>
          {status && <span className="success">{status}</span>}
          {error && <span className="error">{error}</span>}
        </div>
      </form>

      <div className="deliveries-header">
        <h4>Recent deliveries</h4>
        <button type="button" className="link-revoke" onClick={loadDeliveries}>Refresh</button>
      </div>
      {deliveries.length === 0 ? (
        <p className="muted">No deliveries yet.</p>
      ) : (
        <table className="links-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Attempts</th>
              <th>HTTP</th>
              <th>Submission</th>
              <th>When</th>
              <th>Error</th>
            </tr>
          </thead>
          <tbody>
            {deliveries.map((d) => (
              <tr key={d.id}>
                <td>{d.status}</td>
                <td>{d.attempts}</td>
                <td>{d.lastStatusCode ?? '—'}</td>
                <td title={d.submissionId}>{d.submissionId.slice(0, 8)}</td>
                <td>{new Date(d.deliveredAt ?? d.createdAt).toLocaleString()}</td>
                <td className="muted">{d.lastError ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
